import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Paperclip, Plus, Settings, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { showUndoToast } from '@/components/UndoToast';
import DateInput from '@/components/ui/DateInput';
import StatusPill from './StatusPill';
import UserAvatar from './UserAvatar';
import ConfirmModal from '@/components/shared/ConfirmModal';
import { usePermissions } from '@/hooks/usePermissions';
import TaskCommentsTab from './TaskCommentsTab';
import TaskAttachmentsTab from './TaskAttachmentsTab';

function statusOptionsForProject(project) {
  if (!project) return [];
  if (project.project_type === 'waterfall') {
    return [
      { value: 'not_started', label: 'Not started' },
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'in_progress', label: 'In progress' },
      { value: 'blocked', label: 'Blocked' },
      { value: 'waiting_on_customer', label: 'Waiting on Customer' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'complete', label: 'Complete' },
      { value: 'cancelled', label: 'Cancelled' },
    ];
  }
  if (project.project_type === 'agile') {
    return (project.methodology_config?.columns ?? []).map((c) => ({
      value: c.name,
      label: c.name,
    }));
  }
  return [
    { value: 'scoped', label: 'Scoped' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'waiting_on_customer', label: 'Waiting on Customer' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'carried_forward', label: 'Carried forward' },
    { value: 'cancelled', label: 'Cancelled' },
  ];
}

const TABS = [
  { id: 'details', label: 'Details', icon: Settings },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'attachments', label: 'Files', icon: Paperclip },
];

export default function TaskDetailDrawer({ open, onOpenChange, task, project, allTasks, assignees: assigneesProp, onTaskUpdated, onTaskMutated, onTaskDeleted, onConflict }) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('projects.task.edit');
  const canDelete = hasPermission('projects.task.delete');
  const canChangeStatus = hasPermission('projects.task.change_status');
  const canViewComments = hasPermission('projects.task.comment.view');
  const canViewAttachments = hasPermission('projects.task.attachment.view');
  // Logged-hours actuals (this task's planned-vs-actual) are part of the
  // project time module, gated behind `time.view.project` — same switch as the
  // project Time tab. Non-holders don't fetch or see the readout (the backend
  // 403s them anyway); the "Log time" button stays (logging is `time.entry`).
  const canViewProjectTime = hasPermission('time.view.project');

  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [status, setStatus] = useState(task?.status);
  const [priority, setPriority] = useState(task?.priority);
  const [startDate, setStartDate] = useState(task?.start_date ?? '');
  const [dueDate, setDueDate] = useState(task?.due_date ?? '');
  const [assigneeId, setAssigneeId] = useState(task?.assignee_id ?? '');
  const [phase, setPhase] = useState(task?.methodology_fields?.phase ?? '');
  const [estimate, setEstimate] = useState(
    task?.estimated_duration_hours != null ? String(task.estimated_duration_hours) : '',
  );
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false); // unsaved-changes guard on close
  // UI-only: expands truncated label chips to full wrapped text.
  const [labelsExpanded, setLabelsExpanded] = useState(false);
  const [fetchedAssignees, setFetchedAssignees] = useState([]);
  const [showDepPicker, setShowDepPicker] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [loggedHours, setLoggedHours] = useState(null); // Phase C: actual hours logged against this task

  const taskKey = task?.task_id ?? task?._id;

  // Track the freshest updated_at we've observed for this task — either from
  // the parent passing in a new prop OR from a successful PATCH response.
  // Using a ref (not state) means a sequence of quick edits ('change start',
  // then immediately 'change due') uses the right If-Match without waiting
  // for the parent to re-render with the updated task prop.
  const latestUpdatedAtRef = useRef(task?.updated_at);
  useEffect(() => {
    if (task?.updated_at) latestUpdatedAtRef.current = task.updated_at;
  }, [task?.updated_at]);

  useEffect(() => {
    setTitle(task?.title ?? '');
    setDescription(task?.description ?? '');
    setStatus(task?.status);
    setPriority(task?.priority);
    setStartDate(task?.start_date ?? '');
    setDueDate(task?.due_date ?? '');
    setAssigneeId(task?.assignee_id ?? '');
    setPhase(task?.methodology_fields?.phase ?? '');
    setEstimate(task?.estimated_duration_hours != null ? String(task.estimated_duration_hours) : '');
    setSavedAt(null);
    setShowDepPicker(false);
    setActiveTab('details');
    // Per-task UI state: without this, expanding labels on task A leaves
    // task B's labels pre-expanded (the drawer re-renders, never remounts).
    setLabelsExpanded(false);
  }, [taskKey]);

  useEffect(() => { setStatus(task?.status); }, [task?.status]);
  useEffect(() => { setPriority(task?.priority); }, [task?.priority]);
  useEffect(() => { setStartDate(task?.start_date ?? ''); }, [task?.start_date]);
  useEffect(() => { setDueDate(task?.due_date ?? ''); }, [task?.due_date]);
  useEffect(() => { setAssigneeId(task?.assignee_id ?? ''); }, [task?.assignee_id]);
  useEffect(() => { setPhase(task?.methodology_fields?.phase ?? ''); }, [task?.methodology_fields?.phase]);

  // Fetch assignees if parent didn't pass them (so this works from every mount
  // site). Resource governance (project side) — Deal Management backlog §9.1:
  // when the task's project has a linked contract, the fallback pulls the
  // contract-scoped eligible-resources pool, so the assignee picker can only
  // offer people who are on the contract (matching the server guard in
  // projects_task.py). Sites with no contract (or no project) keep the full
  // member list.
  useEffect(() => {
    if (!open) return;
    if (assigneesProp && assigneesProp.length > 0) return;
    const contractId = project?.contract_id;
    const projId = project?.project_id;
    const url = (contractId && projId)
      ? `${API_URL}/api/projects/projects/${projId}/eligible-resources`
      : `${API_URL}/api/projects/assignable-users?role=member`;
    api.get(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        // eligible-resources returns { resources: [...] }; assignable-users
        // returns an array (or { items: [...] }).
        const list = Array.isArray(data)
          ? data
          : (data.resources ?? data.items ?? []);
        setFetchedAssignees(list);
      })
      .catch(() => {});
  }, [open, assigneesProp, project?.contract_id, project?.project_id]);

  const assignees = (assigneesProp && assigneesProp.length > 0) ? assigneesProp : fetchedAssignees;

  // Phase C: fetch this task's logged-hours actuals (planned vs actual). Reads
  // the project rollup and picks out this task's row.
  useEffect(() => {
    const pid = project?.project_id || task?.project_id;
    if (!open || !task?.task_id || !pid || !canViewProjectTime) {
      setLoggedHours(null);
      return;
    }
    let cancelled = false;
    api.get(`${API_URL}/api/time-entries/project-actuals/${pid}`)
      .then(async (r) => {
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const row = (data.by_task || []).find((t) => t.task_id === task.task_id);
        setLoggedHours(row ? row.total_hours : 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, task?.task_id, project?.project_id, task?.project_id, canViewProjectTime]);
  const assigneeMap = useMemo(() => {
    const m = new Map();
    (assignees ?? []).forEach((u) => m.set(u.user_id ?? u._id, u));
    return m;
  }, [assignees]);

  // Do the local text-field values differ from what's persisted? Drives the
  // Save button's enabled state AND the unsaved-changes close guard. Estimate
  // compares the parsed number so "3" vs 3 isn't flagged dirty. Optional
  // chaining because this now runs *before* the `!task` early return below.
  const estNum = estimate.trim() === '' ? null : Number(estimate.trim());
  const estDirty =
    !(estNum !== null && (Number.isNaN(estNum) || estNum < 0)) &&
    estNum !== (task?.estimated_duration_hours ?? null);
  const textDirty =
    title.trim() !== (task?.title ?? '') ||
    (description ?? '') !== (task?.description ?? '') ||
    estDirty;

  // Esc closes the drawer, routed through the same dirty-guard as the backdrop
  // and the X button. If a confirm dialog is already up, Esc dismisses that
  // first (ConfirmModal has no Esc handling of its own). stopPropagation keeps
  // a stacked parent dialog from also reacting to the same keypress.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (deleteConfirm) { setDeleteConfirm(false); return; }
      if (discardConfirm) { setDiscardConfirm(false); return; }
      if (textDirty) setDiscardConfirm(true);
      else onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, textDirty, deleteConfirm, discardConfirm, onOpenChange]);

  if (!open || !task) return null;

  const taskId = task.task_id ?? task._id;

  // User-initiated close (backdrop, X button). Guards unsaved text edits with
  // a discard confirm; programmatic closes after save/delete bypass this and
  // call onOpenChange(false) directly.
  const requestClose = () => {
    if (textDirty) setDiscardConfirm(true);
    else onOpenChange(false);
  };

  const patch = async (partial, opts = {}) => {
    setSaving(true);
    try {
      // `ifMatch: null` means force-write (used by undo paths). `??` would
      // coalesce null back to the ref, so an explicit `in` check is needed.
      // Otherwise read from the ref so quick sequential edits ('start',
      // then immediately 'due') don't race the parent's re-render.
      const ifMatchValue = 'ifMatch' in opts ? opts.ifMatch : (latestUpdatedAtRef.current ?? null);
      const res = await api.patchConditional(
        `${API_URL}/api/projects/tasks/${taskId}`,
        partial,
        ifMatchValue,
      );
      const data = await res.json().catch(() => ({}));

      if (res.status === 409) {
        // Conflict: another user edited this task concurrently.
        if (onConflict) {
          onConflict({
            myDoc: { ...task, ...partial },
            theirDoc: data?.current_doc ?? null,
            retryFn: () => patch(partial, { ifMatch: null }),
          });
        } else {
          toast.error('Editing conflict — another user changed this task. Refresh and retry.');
        }
        return null;
      }

      if (!res.ok) {
        const detail = typeof data?.detail === 'string'
          ? data.detail
          : data?.detail?.message ?? 'Could not update task.';
        toast.error(detail);
        return null;
      }
      // Roll our own observation of updated_at forward before notifying
      // the parent — the next sequential edit needs the fresh value
      // immediately, not after React schedules the re-render round-trip.
      if (data?.updated_at) latestUpdatedAtRef.current = data.updated_at;
      setSavedAt(new Date());
      if (onTaskMutated) {
        onTaskMutated(data);
      } else {
        onTaskUpdated?.();
      }
      return data;
    } catch {
      toast.error('Could not update task.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Explicit save for the free-text fields (Title / Description / Estimate).
  // Unlike the selects/dates — which still auto-save on change with an undo
  // toast — these commit only when the user clicks Save, so typing never fires
  // a PATCH mid-edit. Builds a minimal diff and sends it in one request.
  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.error('Title is required.');
      return;
    }
    const rawEst = estimate.trim();
    const nextEst = rawEst === '' ? null : Number(rawEst);
    if (nextEst !== null && (Number.isNaN(nextEst) || nextEst < 0)) {
      toast.error('Estimated hours must be a number ≥ 0.');
      return;
    }

    const diff = {};
    if (trimmedTitle !== (task.title ?? '')) diff.title = trimmedTitle;
    if ((description ?? '') !== (task.description ?? '')) diff.description = description;
    if (nextEst !== (task.estimated_duration_hours ?? null)) {
      diff.estimated_duration_hours = nextEst;
    }
    if (Object.keys(diff).length === 0) {
      onOpenChange(false); // nothing to save — just close
      return;
    }

    // Close only on success — patch() returns the updated doc, or null on a
    // validation error / 409 conflict (which keeps the drawer open to retry).
    const result = await patch(diff);
    if (result) onOpenChange(false);
  };

  const handleDelete = async () => {
    const res = await api.delete(`${API_URL}/api/projects/tasks/${taskId}`);
    if (!res.ok) { toast.error('Could not delete task.'); return; }
    toast.success('Task deleted');
    onOpenChange(false);
    if (onTaskDeleted) {
      onTaskDeleted(taskId);
    } else {
      onTaskUpdated?.();
    }
  };

  const addDependency = async (depId) => {
    if (!depId) return;
    try {
      const res = await api.post(`${API_URL}/api/projects/tasks/${taskId}/dependencies`, { depends_on_task_id: depId });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const detail = typeof data?.detail === 'string'
          ? data.detail
          : data?.detail?.message ?? 'Could not add dependency.';
        toast.error(detail);
        return;
      }
      toast.success('Dependency added');
      setShowDepPicker(false);
      if (data && (data.task_id || data._id)) {
        onTaskMutated?.(data);
      } else {
        onTaskUpdated?.();
      }
    } catch {
      toast.error('Could not add dependency.');
    }
  };

  const removeDependency = async (depId) => {
    try {
      const res = await api.delete(`${API_URL}/api/projects/tasks/${taskId}/dependencies/${depId}`);
      if (!res.ok) {
        toast.error('Could not remove dependency.');
        return;
      }
      const data = await res.json().catch(() => null);
      toast.success('Dependency removed');
      if (data && (data.task_id || data._id)) {
        onTaskMutated?.(data);
      } else {
        onTaskUpdated?.();
      }
    } catch {
      toast.error('Could not remove dependency.');
    }
  };

  const statusOptions = statusOptionsForProject(project);
  const phases = project?.methodology_config?.phases ?? [];
  const mf = task.methodology_fields ?? {};

  // Filter tabs by perm — viewers without comment/attachment perms see fewer.
  const visibleTabs = TABS.filter((t) => {
    if (t.id === 'comments') return canViewComments;
    if (t.id === 'attachments') return canViewAttachments;
    return true;
  });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-30"
        onClick={requestClose}
        aria-hidden="true"
        data-testid="task-drawer-backdrop"
      />

      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 w-[420px] z-40 bg-boss-surface border-l border-boss-border shadow-lg overflow-y-auto"
        data-testid={`task-drawer-${taskId}`}
        role="dialog"
        aria-label="Task details"
      >
        <header className="px-4 py-3 border-b border-boss-border flex items-center gap-2 sticky top-0 bg-boss-surface z-10">
          <span className="font-mono text-xs text-boss-text-muted">
            #{task.task_number ?? '—'}
          </span>
          <span className="text-sm font-semibold text-boss-text">Task details</span>
          {savedAt ? (
            <span className="ml-auto text-[11px] text-boss-text-muted" data-testid="drawer-saved">
              Saved · {savedAt.toLocaleTimeString()}
            </span>
          ) : null}
          <button
            type="button"
            onClick={requestClose}
            className="ml-auto h-6 w-6 inline-flex items-center justify-center text-boss-text-muted hover:text-boss-text"
            aria-label="Close drawer"
          >
            <X size={14} />
          </button>
        </header>

        {/* Tab strip — shows 1-3 tabs based on perms; degrades to invisible when only Details */}
        {visibleTabs.length > 1 ? (
          <div
            className="flex items-center gap-1 px-2 pt-2 border-b border-boss-border bg-boss-surface"
            role="tablist"
            data-testid="drawer-tabs"
          >
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.id)}
                  className={`h-8 px-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-t border-b-2 transition-colors ${
                    active
                      ? 'border-violet-400 text-boss-text'
                      : 'border-transparent text-boss-text-muted hover:text-boss-text'
                  }`}
                  data-testid={`drawer-tab-${t.id}`}
                >
                  <Icon size={12} />
                  {t.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="p-4 space-y-4">
          {activeTab === 'details' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-title">
                  Title
                </label>
                <input
                  id="drawer-title"
                  className="boss-input w-full h-9 px-3 text-sm disabled:opacity-60"
                  value={title}
                  disabled={!canEdit || saving}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="drawer-title"
                />
              </div>

              {/* Phase C: logged-time actuals (planned vs actual) + log-time deep link */}
              {/* `!my-5` overrides the parent `space-y-4` (16px) to give this
                  bordered card 20px of breathing room above (off the Title input)
                  and below (above the Description label). The `!` is required —
                  `space-y-*`'s `> * ~ *` selector out-specifies a plain `mt-*`. */}
              <div className="rounded-lg border border-boss-border bg-boss-elevated/40 p-3 !my-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs text-boss-text-muted">
                    {canViewProjectTime && (
                      <>
                        Logged time
                        <span className="ml-2 text-sm font-semibold text-boss-text">
                          {loggedHours == null ? '…' : `${loggedHours}h`}
                        </span>
                        {task?.estimated_duration_hours != null && (
                          <span className="ml-1">/ {task.estimated_duration_hours}h est.</span>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const pid = project?.project_id || task?.project_id;
                      const params = new URLSearchParams();
                      if (pid) params.set('project_id', pid);
                      if (task?.task_id) params.set('task_id', task.task_id);
                      window.location.href = `/time-management?${params.toString()}`;
                    }}
                    className="px-2.5 py-1 text-xs rounded-md bg-violet-600 text-white hover:bg-violet-700 transition shrink-0"
                    data-testid="drawer-log-time"
                  >
                    Log time
                  </button>
                </div>
                {canViewProjectTime &&
                  task?.estimated_duration_hours != null &&
                  task.estimated_duration_hours > 0 &&
                  loggedHours != null && (
                    <div className="mt-2 h-1.5 rounded-full bg-boss-border overflow-hidden">
                      <div
                        className="h-full bg-violet-500"
                        style={{
                          width: `${Math.min(100, (loggedHours / task.estimated_duration_hours) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
              </div>

              <div>
                <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-description">
                  Description
                </label>
                <textarea
                  id="drawer-description"
                  rows={4}
                  className="boss-input w-full px-3 py-2 resize-y text-sm disabled:opacity-60"
                  value={description ?? ''}
                  disabled={!canEdit || saving}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="drawer-description"
                  placeholder="Add a description…"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-status">
                    Status
                  </label>
                  <select
                    id="drawer-status"
                    value={status ?? ''}
                    disabled={!canChangeStatus || saving}
                    onChange={async (e) => {
                      const next = e.target.value;
                      const prev = status;
                      setStatus(next);
                      const ok = await patch({ status: next });
                      if (!ok) {
                        setStatus(prev);
                      } else {
                        showUndoToast({
                          message: `Status changed to "${next}"`,
                          onUndo: async () => {
                            setStatus(prev);
                            // Force-write on undo: the user explicitly asked
                            // to revert, so skip the If-Match concurrency
                            // check (the closure's task.updated_at is stale
                            // after the original mutation, which would
                            // otherwise trigger a spurious 409).
                            await patch({ status: prev }, { ifMatch: null });
                          },
                        });
                      }
                    }}
                    className="boss-input w-full h-9 px-3 text-sm disabled:opacity-60"
                    data-testid="drawer-status"
                  >
                    {statusOptions.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <div className="mt-1">
                    <StatusPill status={status} size="sm" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-priority">
                    Priority
                  </label>
                  <select
                    id="drawer-priority"
                    value={priority ?? ''}
                    disabled={!canEdit || saving}
                    onChange={async (e) => {
                      const next = e.target.value;
                      const prev = priority;
                      setPriority(next);
                      const ok = await patch({ priority: next });
                      if (!ok) {
                        setPriority(prev);
                      } else {
                        showUndoToast({
                          message: `Priority changed to "${next}"`,
                          onUndo: async () => {
                            setPriority(prev);
                            await patch({ priority: prev }, { ifMatch: null });
                          },
                        });
                      }
                    }}
                    className="boss-input w-full h-9 px-3 text-sm disabled:opacity-60"
                    data-testid="drawer-priority"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-start-date">
                    Start date
                  </label>
                  <DateInput
                    id="drawer-start-date"
                    value={startDate}
                    disabled={!canEdit || saving}
                    onChange={async (next) => {
                      const prev = startDate;
                      setStartDate(next);
                      const ok = await patch({ start_date: next || null });
                      if (!ok) setStartDate(prev);
                    }}
                    data-testid="drawer-start-date"
                    placeholder="No start date"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-due-date">
                    Due date
                  </label>
                  <DateInput
                    id="drawer-due-date"
                    value={dueDate}
                    disabled={!canEdit || saving}
                    onChange={async (next) => {
                      const prev = dueDate;
                      setDueDate(next);
                      const ok = await patch({ due_date: next || null });
                      if (!ok) setDueDate(prev);
                    }}
                    data-testid="drawer-due-date"
                    placeholder="No due date"
                    min={startDate || undefined}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-estimate">
                    Estimated hours
                  </label>
                  <input
                    id="drawer-estimate"
                    type="number"
                    min="0"
                    step="0.25"
                    value={estimate}
                    disabled={!canEdit || saving}
                    onChange={(e) => setEstimate(e.target.value)}
                    className="boss-input w-full h-9 px-3 text-sm disabled:opacity-60"
                    placeholder="No estimate"
                    data-testid="drawer-estimate"
                  />
                </div>

                {(task?.labels?.length ?? 0) > 0 ? (
                  <div className="col-span-2" data-testid="drawer-labels">
                    <span className="block text-xs font-medium text-boss-text-muted mb-1">
                      Labels
                    </span>
                    {/* Read-only: free-form tags, including the paste-
                        provenance tag (source project name) from bundle paste. */}
                    <div className="flex flex-wrap gap-1">
                      {task.labels.map((l) => (
                        <span
                          key={l}
                          title={l}
                          className={`inline-flex max-w-full items-center rounded bg-boss-elevated px-2 py-0.5 text-[11px] text-boss-text-secondary ${
                            labelsExpanded ? 'whitespace-normal break-all' : 'truncate'
                          }`}
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                    {/* Unconditional (user choice): always offer the full-text
                        view, since a provenance label is no longer
                        distinguishable from any other label. */}
                    <button
                      type="button"
                      onClick={() => setLabelsExpanded((v) => !v)}
                      className="mt-1 text-[11px] text-boss-text-muted hover:text-boss-text underline-offset-2 hover:underline"
                      data-testid="drawer-labels-toggle"
                    >
                      {labelsExpanded ? 'Show less' : 'Show full labels'}
                    </button>
                  </div>
                ) : null}

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-assignee">
                    Assignee
                  </label>
                  <select
                    id="drawer-assignee"
                    value={assigneeId}
                    disabled={!canEdit || saving}
                    onChange={async (e) => {
                      const next = e.target.value;
                      const prev = assigneeId;
                      setAssigneeId(next);
                      const ok = await patch({ assignee_id: next || null });
                      if (!ok) {
                        setAssigneeId(prev);
                      } else {
                        const nextName = next
                          ? (assigneeMap.get(next)?.name ?? assigneeMap.get(next)?.display_name ?? 'someone')
                          : 'Unassigned';
                        showUndoToast({
                          message: `Assigned to ${nextName}`,
                          onUndo: async () => {
                            setAssigneeId(prev);
                            await patch({ assignee_id: prev || null }, { ifMatch: null });
                          },
                        });
                      }
                    }}
                    className="boss-input w-full h-9 px-3 text-sm disabled:opacity-60"
                    data-testid="drawer-assignee"
                  >
                    <option value="">— Unassigned —</option>
                    {(assignees ?? [])
                      .slice()
                      .sort((a, b) => (a.name ?? a.display_name ?? '').localeCompare(b.name ?? b.display_name ?? ''))
                      .map((u) => (
                        <option key={u.user_id ?? u._id} value={u.user_id ?? u._id}>
                          {u.name ?? u.display_name ?? u.user_id ?? u._id}
                        </option>
                      ))}
                  </select>
                  {assigneeId && assigneeMap.get(assigneeId) ? (
                    <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-boss-text-muted">
                      <UserAvatar user={assigneeMap.get(assigneeId)} size="sm" />
                      <span>{assigneeMap.get(assigneeId).name ?? assigneeMap.get(assigneeId).display_name}</span>
                    </div>
                  ) : null}
                </div>

                {project?.project_type === 'waterfall' && phases.length > 0 ? (
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-boss-text-muted mb-1" htmlFor="drawer-phase">
                      Phase
                    </label>
                    <select
                      id="drawer-phase"
                      value={phase}
                      disabled={!canEdit || saving}
                      onChange={async (e) => {
                        const next = e.target.value;
                        const prev = phase;
                        setPhase(next);
                        const ok = await patch({ methodology_fields: { ...mf, phase: next } });
                        if (!ok) setPhase(prev);
                      }}
                      className="boss-input w-full h-9 px-3 text-sm disabled:opacity-60"
                      data-testid="drawer-phase"
                    >
                      {phases.map((p) => {
                        // phases may be List[str] (legacy) or List[Dict[name,...]] (canonical)
                        const name = typeof p === 'string' ? p : p?.name;
                        if (!name) return null;
                        return <option key={name} value={name}>{name}</option>;
                      })}
                    </select>
                  </div>
                ) : null}
              </div>

              <div data-testid="drawer-dependencies">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-boss-text-muted">Dependencies</p>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => setShowDepPicker((v) => !v)}
                      className="h-6 px-2 inline-flex items-center gap-1 text-[11px] text-boss-text-muted hover:text-boss-text border border-boss-border rounded"
                      data-testid="drawer-dep-add-toggle"
                    >
                      <Plus size={11} /> Add
                    </button>
                  ) : null}
                </div>
                {(task.dependencies ?? []).length === 0 ? (
                  <p className="text-[11px] text-boss-text-muted italic">No dependencies.</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {task.dependencies.map((depId) => {
                      const dep = (allTasks ?? []).find(
                        (t) => (t.task_id ?? t._id) === depId,
                      );
                      return (
                        <li
                          key={depId}
                          className="flex items-center gap-2 px-2 py-1 rounded bg-boss-elevated"
                          data-testid={`drawer-dep-row-${depId}`}
                        >
                          <span className="font-mono text-boss-text-muted">
                            #{dep?.task_number ?? '—'}
                          </span>
                          <span className="truncate text-boss-text flex-1">
                            {dep?.title ?? '(deleted)'}
                          </span>
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => removeDependency(depId)}
                              className="h-5 w-5 inline-flex items-center justify-center text-boss-text-muted hover:text-red-400"
                              aria-label={`Remove dependency #${dep?.task_number ?? ''}`}
                              data-testid={`drawer-dep-remove-${depId}`}
                            >
                              <Trash2 size={10} />
                            </button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {showDepPicker && canEdit ? (
                  <div className="mt-2">
                    <select
                      defaultValue=""
                      onChange={(e) => addDependency(e.target.value)}
                      className="boss-input w-full h-9 px-3 text-sm"
                      data-testid="drawer-dep-picker"
                    >
                      <option value="">— Pick a task to depend on —</option>
                      {(allTasks ?? [])
                        .filter((t) => (t.task_id ?? t._id) !== taskId)
                        .filter((t) => !(task.dependencies ?? []).includes(t.task_id ?? t._id))
                        .map((t) => (
                          <option key={t.task_id ?? t._id} value={t.task_id ?? t._id}>
                            #{t.task_number ?? '—'} · {t.title}
                          </option>
                        ))}
                    </select>
                  </div>
                ) : null}
              </div>

              {/* Unified action footer — Delete (left) + Save (right) on one
                  level. Sticky so both stay reachable while scrolling; a soft
                  top shadow lifts it off the content. pr-24 keeps Save clear
                  of the floating BOSS AI button. */}
              {(canDelete || canEdit) ? (
                <div className="sticky bottom-0 -mx-4 mt-2 px-4 py-3 bg-boss-surface border-t border-boss-border shadow-[0_-8px_16px_-12px_rgba(0,0,0,0.5)] flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    {canDelete ? (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(true)}
                        className="h-8 shrink-0 px-3 rounded-md text-xs font-medium whitespace-nowrap text-red-400 border border-red-500/20 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300 active:bg-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 transition-colors"
                        data-testid="drawer-delete"
                      >
                        Delete
                      </button>
                    ) : null}
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!textDirty || saving}
                        className="h-8 shrink-0 px-4 rounded-md bg-violet-600 text-white text-xs font-medium whitespace-nowrap shadow-sm hover:bg-violet-700 active:bg-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed transition-colors"
                        data-testid="drawer-save"
                      >
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                    ) : null}
                  </div>
                  {canEdit && textDirty ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-medium whitespace-nowrap text-amber-400" data-testid="drawer-dirty" title="Unsaved changes">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" aria-hidden="true" />
                      Unsaved changes
                    </span>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}

          {activeTab === 'comments' && canViewComments ? (
            <TaskCommentsTab
              task={task}
              assignees={assignees}
              onChanged={onTaskUpdated}
            />
          ) : null}

          {activeTab === 'attachments' && canViewAttachments ? (
            <TaskAttachmentsTab
              task={task}
              assignees={assignees}
              onChanged={onTaskUpdated}
            />
          ) : null}
        </div>
      </aside>

      <ConfirmModal
        isOpen={deleteConfirm}
        title="Delete task"
        message={`Delete "${task.title}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(false)}
      />

      <ConfirmModal
        isOpen={discardConfirm}
        title="Discard changes?"
        message="This task has unsaved changes. Close the drawer and discard them?"
        confirmText="Discard"
        cancelText="Keep editing"
        variant="danger"
        onConfirm={() => onOpenChange(false)}
        onCancel={() => setDiscardConfirm(false)}
      />
    </>
  );
}
